// src/pages/Liability.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Edit, Trash2, TrendingDown, Save, X, RefreshCw, Search, Download, Upload } from 'lucide-react';
import { LookupValue } from '../services/lookupService';
import { deleteLiabilityRecord, fetchLiabilityData, saveOrUpdateLiabilityRecord } from '../services/liabilityService'; 
import { fetchDependents } from '../services/assetService';
import { useLookups } from '../context/LookupContext';
import { useAppContext } from '../context/AppContext';

// Import Child Components
import Notification from '../components/shared/Notification';
import LiabilityHeader from '../components/liability/LiabilityHeader';
import LiabilityForm from '../components/liability/LiabilityForm';
import LiabilitiesTable from '../components/liability/LiabilitiesTable';
import LiabilitiesMobileView from '../components/liability/LiabilitiesMobileView';
import ActionBar from '../components/liability/ActionBar';
import EmptyState from '../components/liability/EmptyState';
import Pagination from '../components/shared/Pagination';

export interface LiabilityData {
  id: string;
  liabilityOwnerType: string;
  liabilityType: string;
  liabilitySubType: string;
  liabilityStartDate: string;
  liabilityEndDate: string;
  liabilityTenure: string;
  liabilityTotalAmount: string;
  liabilityOutstandingAmount: string;
  liabilityInterestRate: string;
  liabilityInstalmentAmount: string;
  liabilityFrequency: string;
  status: string;
  familyMemberId?: string;
}

export type ColumnKey = keyof LiabilityData | 'ownerTypeLabel' | 'typeLabel' | 'subTypeLabel' | 'frequencyLabel';

const visibleColumns: ColumnKey[] = [
  'liabilityOwnerType', 'liabilityType', 'liabilitySubType', 'liabilityTotalAmount',
  'liabilityOutstandingAmount', 'liabilityInstalmentAmount', 'liabilityFrequency',
  'liabilityStartDate', 'liabilityEndDate', 'liabilityInterestRate', 'liabilityTenure',
];

const getColumnDisplayName = (col: ColumnKey): string => {
  const displayNames: Record<ColumnKey, string> = {
    id: 'ID', liabilityOwnerType: 'Owner Type', liabilityType: 'Type', liabilitySubType: 'Sub Type',
    liabilityStartDate: 'Start Date', liabilityEndDate: 'End Date', liabilityTenure: 'Tenure (Months)',
    liabilityTotalAmount: 'Total Amount', liabilityOutstandingAmount: 'Outstanding Amount',
    liabilityInterestRate: 'Interest Rate (%)', liabilityInstalmentAmount: 'Installment',
    liabilityFrequency: 'Frequency', status: 'Status', familyMemberId: '',
    ownerTypeLabel: 'Owner Type', typeLabel: 'Type', subTypeLabel: 'Sub Type', frequencyLabel: 'Frequency',
  };
  return displayNames[col];
};

const Liability: React.FC = () => {
    const { state } = useAppContext();
    const { getOptions, getLabel, isLoading: isLookupsLoading } = useLookups();
    
    const [dependents, setDependents] = useState<{ id: string; name: string }[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [apiLoading, setApiLoading] = useState(true);
    const [transformedData, setTransformedData] = useState<LiabilityData[]>([]);
    const [operationStatus, setOperationStatus] = useState({ show: false, message: '', type: 'success' as 'success' | 'error' });
    const [formData, setFormData] = useState<LiabilityData>({
        id: '', liabilityOwnerType: '', liabilityType: '', liabilitySubType: '', liabilityStartDate: '',
        liabilityEndDate: '', liabilityTenure: '', liabilityTotalAmount: '', liabilityOutstandingAmount: '',
        liabilityInterestRate: '', liabilityInstalmentAmount: '', liabilityFrequency: '', status: '', familyMemberId: ''
    });
    const [dropdownOptions, setDropdownOptions] = useState<{ ownerType: LookupValue[]; liabilityType: LookupValue[]; liabilitySubType: LookupValue[]; liabilityFrequency: LookupValue[]; }>({
        ownerType: [], liabilityType: [], liabilitySubType: [], liabilityFrequency: []
    });
    const [loadingSubTypes, setLoadingSubTypes] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: ColumnKey; direction: 'asc' | 'desc' | null; }>({ key: 'liabilityType', direction: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [columnFilters, setColumnFilters] = useState<Partial<Record<ColumnKey, string>>>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);

    const lookupGroupMap: Record<string, string> = {
        liabilityOwnerType: 'ownerType', liabilityType: 'liabilityType',
        liabilitySubType: 'liabilitySubType', liabilityFrequency: 'liabilityFrequency',
    };

    const formatCurrency = useCallback((value: string | number) => {
        const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
        if (isNaN(num)) return 'N/A';
        const symbol = state.currencySymbol || '₹';
        return `${symbol}${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.floor(num))}`;
    }, [state.currencySymbol]);

    const formatDate = (date: string) => {
        if (!date) return 'N/A';
        try {
            const d = new Date(date);
            if (isNaN(d.getTime())) return 'N/A';
            const adjustedDate = new Date(d.valueOf() + d.getTimezoneOffset() * 60 * 1000);
            return `${String(adjustedDate.getDate()).padStart(2, '0')}/${String(adjustedDate.getMonth() + 1).padStart(2, '0')}/${adjustedDate.getFullYear()}`;
        } catch { return date; }
    };
    
    const resetForm = () => {
        setFormData({
            id: '', liabilityOwnerType: '', liabilityType: '', liabilitySubType: '', liabilityStartDate: '',
            liabilityEndDate: '', liabilityTenure: '', liabilityTotalAmount: '', liabilityOutstandingAmount: '',
            liabilityInterestRate: '', liabilityInstalmentAmount: '', liabilityFrequency: '', status: '', familyMemberId: ''
        });
        setEditingId(null);
    };

    const fetchLiabilityFromAPI = useCallback(async () => {
        setApiLoading(true);
        try {
            const apiData = await fetchLiabilityData();
            if (!apiData || !Array.isArray(apiData.data)) throw new Error('Invalid API response structure');
            setTransformedData(apiData.data as LiabilityData[]);
        } catch (error) {
            console.error('Failed to fetch liability data:', error);
            setOperationStatus({ show: true, message: 'Failed to fetch liabilities.', type: 'error' });
        } finally {
            setApiLoading(false);
        }
    }, []);

    useEffect(() => { fetchLiabilityFromAPI(); }, [fetchLiabilityFromAPI]);
    useEffect(() => { fetchDependents().then(setDependents); }, []);

    useEffect(() => {
        if (!isLookupsLoading) {
            setDropdownOptions({
                ownerType: getOptions('ownerType'),
                liabilityType: getOptions('liabilityType'),
                liabilityFrequency: getOptions('liabilityFrequency'),
                liabilitySubType: [],
            });
        }
    }, [isLookupsLoading, getOptions]);

    useEffect(() => {
        const typeVal = formData.liabilityType;
        if (!typeVal) {
            setDropdownOptions(prev => ({ ...prev, liabilitySubType: [] }));
            return;
        }
        setLoadingSubTypes(true);
        const allSubTypes = getOptions('liabilitySubType');
        const filteredSubs = allSubTypes.filter(sub => sub.parentValue === typeVal);
        setDropdownOptions(prev => ({ ...prev, liabilitySubType: filteredSubs }));
        setLoadingSubTypes(false);
    }, [formData.liabilityType, getOptions]);
    
    useEffect(() => {
        if (operationStatus.show) {
            const timer = setTimeout(() => setOperationStatus({ show: false, message: '', type: 'success' }), 5000);
            return () => clearTimeout(timer);
        }
    }, [operationStatus]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value, ...(name === 'liabilityOwnerType' && { familyMemberId: '' }) }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.liabilityOwnerType || !formData.liabilityType || !formData.liabilitySubType || !formData.liabilityInstalmentAmount) {
            return alert('Please fill in all required fields marked with *');
        }
        if (formData.liabilityOwnerType === '2' && !formData.familyMemberId) {
            return alert('Please select a family member for "Family" liabilities.');
        }
        setIsFormSubmitted(true);
        try {
            await saveOrUpdateLiabilityRecord(formData);
            setShowForm(false);
            resetForm();
            await fetchLiabilityFromAPI();
            setOperationStatus({ show: true, message: `Liability ${editingId ? 'updated' : 'created'} successfully!`, type: 'success' });
        } catch (error: any) {
            setOperationStatus({ show: true, message: error.message || 'Failed to save liability.', type: 'error' });
        } finally {
            setIsFormSubmitted(false);
        }
    };
    
    const handleSort = (key: ColumnKey) => {
        let direction: 'asc' | 'desc' = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        setSortConfig({ key, direction });
        const sortedData = [...transformedData].sort((a, b) => {
            const group = lookupGroupMap[key];
            let aValue: string | number | Date = group ? getLabel(group, a[key as keyof LiabilityData] as string) : a[key as keyof LiabilityData] ?? '';
            let bValue: string | number | Date = group ? getLabel(group, b[key as keyof LiabilityData] as string) : b[key as keyof LiabilityData] ?? '';

            if (['liabilityTotalAmount', 'liabilityOutstandingAmount', 'liabilityInterestRate', 'liabilityInstalmentAmount', 'liabilityTenure'].includes(key)) {
                aValue = parseFloat(aValue as string || '0');
                bValue = parseFloat(bValue as string || '0');
            } else if (['liabilityStartDate', 'liabilityEndDate'].includes(key)) {
                aValue = new Date(aValue as string);
                bValue = new Date(bValue as string);
            }

            if (aValue < bValue) return direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            return 0;
        });
        setTransformedData(sortedData);
    };
    
    const handleEdit = (liability: LiabilityData) => { setFormData(liability); setEditingId(liability.id); setShowForm(true); };
    
    // ✅ CORRECTED DELETE LOGIC
    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this liability record?')) {
            try {
                await deleteLiabilityRecord(id);
                setTransformedData(prevData => prevData.filter(item => item.id !== id));
                setSelectedRows(prev => prev.filter(rowId => rowId !== id));
                setOperationStatus({ show: true, message: 'Liability record deleted successfully!', type: 'success' });
            } catch (error: any) {
                console.error('Delete failed:', error);
                setOperationStatus({ show: true, message: error.message || 'Failed to delete liability record.', type: 'error' });
            }
        }
    };

    // ✅ CORRECTED BULK DELETE LOGIC
    const handleBulkDelete = async () => {
        if (selectedRows.length === 0) return;
        if (window.confirm(`Are you sure you want to delete ${selectedRows.length} selected records?`)) {
            try {
                await Promise.all(selectedRows.map(id => deleteLiabilityRecord(id)));
                setTransformedData(prevData => prevData.filter(item => !selectedRows.includes(item.id)));
                setSelectedRows([]);
                setOperationStatus({ show: true, message: 'Selected records deleted successfully!', type: 'success' });
            } catch (error: any) {
                console.error('Bulk delete failed:', error);
                setOperationStatus({ show: true, message: error.message || 'Failed to delete selected records.', type: 'error' });
            }
        }
    };

    const handleRowSelect = (id: string) => setSelectedRows(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
    const handleSelectAll = () => setSelectedRows(selectedRows.length === paginatedData.length ? [] : paginatedData.map(d => d.id));
    
   
    const filteredData = transformedData.filter(liability => {
        const getDisplayValue = (colKey: keyof LiabilityData) => {
            const group = lookupGroupMap[colKey];
            return group ? getLabel(group, liability[colKey] as string) : liability[colKey] || '';
        };
        const searchMatch = searchTerm ? Object.keys(liability).some(key => String(getDisplayValue(key as keyof LiabilityData)).toLowerCase().includes(searchTerm.toLowerCase())) : true;
        const filterMatch = Object.entries(columnFilters).every(([key, value]) => !value || String(getDisplayValue(key as keyof LiabilityData)).toLowerCase().includes(String(value).toLowerCase()));
        return searchMatch && filterMatch;
    });

    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);
    const totalLiability = transformedData.reduce((sum, item) => sum + parseFloat(item.liabilityOutstandingAmount || '0'), 0);

    const paginationProps = {
        currentPage, totalPages, totalItems, itemsPerPage, startIndex, endIndex,
        handlePageChange: setCurrentPage,
        handleItemsPerPageChange: (e: React.ChangeEvent<HTMLSelectElement>) => { setItemsPerPage(parseInt(e.target.value)); setCurrentPage(1); },
    };

    const getSortIndicator = (key: ColumnKey) => (sortConfig.key === key ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '');
    const handleAddClick = () => { resetForm(); setShowForm(true); };
    
    const renderDropdownOptions = (options: LookupValue[], placeholder = 'Select...') => {
        if (isLookupsLoading) return <option>Loading...</option>;
        if (!options || options.length === 0) return <option>No options available</option>;
        return (<><option value="">{placeholder}</option>{options.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}</>);
    };

    return (
        <div className="space-y-8 p-4 sm:p-6">
            <Notification status={operationStatus} onClose={() => setOperationStatus({ ...operationStatus, show: false })} />
            
            <LiabilityHeader
                totalLiability={totalLiability}
                recordCount={filteredData.length}
                apiLoading={apiLoading}
                onAdd={handleAddClick}
                formatCurrency={formatCurrency}
            />

            {showForm && (
                <LiabilityForm
                    formData={formData}
                    editingId={editingId}
                    isFormSubmitted={isFormSubmitted}
                    dropdownOptions={dropdownOptions}
                    dependents={dependents}
                    loadingSubTypes={loadingSubTypes}
                    handleSubmit={handleSubmit}
                    handleInputChange={handleInputChange}
                    handleSelectChange={handleSelectChange}
                    onCancel={() => { setShowForm(false); resetForm(); }}
                    renderDropdownOptions={renderDropdownOptions}
                />
            )}
            
            <LiabilitiesMobileView
                paginatedData={paginatedData}
                apiLoading={apiLoading}
                searchTerm={searchTerm}
                onSearchChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                getLabel={getLabel}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                paginationProps={paginationProps}
            />
               {filteredData.length >0 && (
            <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-x-auto">
                <ActionBar
                    searchTerm={searchTerm}
                    onSearchChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    onBulkDelete={handleBulkDelete}
                    selectedRowCount={selectedRows.length}
                />
                  {apiLoading ? (
                  <div className="text-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-green-600" /></div>
                  ) : (
                    <LiabilitiesTable
                        paginatedData={paginatedData}
                        visibleColumns={visibleColumns}
                        getColumnDisplayName={getColumnDisplayName}
                        getSortIndicator={getSortIndicator}
                        handleSort={handleSort}
                        columnFilters={columnFilters}
                        setColumnFilters={setColumnFilters}
                        selectedRows={selectedRows}
                        handleSelectAll={handleSelectAll}
                        handleRowSelect={handleRowSelect}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        formatCurrency={formatCurrency}
                        formatDate={formatDate}
                        getLabel={getLabel}
                        lookupGroupMap={lookupGroupMap}
                    />
                )}
                <Pagination {...paginationProps} />
            </div>
                )}

            {filteredData.length === 0 && !apiLoading && (
                <EmptyState onAdd={handleAddClick} />
            )}
        </div>
    );
};

export default Liability;